import { RRule } from 'rrule';

export type ExpenseRecurrencePreset =
  | 'none'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'yearly';

export const EXPENSE_RECURRENCE_OPTIONS: {
  label: string;
  value: ExpenseRecurrencePreset;
}[] = [
  { label: 'Does not repeat', value: 'none' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Every 2 weeks', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
];

function toIcalUtcTimestamp(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

export function parseUntilFromRrule(rrule: string): Date | null {
  const token = rrule
    .split(';')
    .find((part) => part.startsWith('UNTIL='))
    ?.slice('UNTIL='.length);
  if (!token) return null;
  const match = token.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    )
  );
}

export function parseExpenseRecurrencePreset(
  rrule: string | null | undefined
): ExpenseRecurrencePreset {
  if (!rrule) return 'none';
  if (rrule.includes('FREQ=YEARLY')) return 'yearly';
  if (rrule.includes('FREQ=MONTHLY')) return 'monthly';
  if (rrule.includes('FREQ=WEEKLY') && rrule.includes('INTERVAL=2')) {
    return 'biweekly';
  }
  if (rrule.includes('FREQ=WEEKLY')) return 'weekly';
  return 'none';
}

export function buildExpenseRrule(
  preset: ExpenseRecurrencePreset,
  startAt: Date,
  recurrenceEndDate: Date | null
): string | null {
  if (preset === 'none') return null;
  const weekdayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  const byDay = weekdayMap[startAt.getDay()];
  let baseRule: string | null = null;
  switch (preset) {
    case 'weekly':
      baseRule = `FREQ=WEEKLY;BYDAY=${byDay}`;
      break;
    case 'biweekly':
      baseRule = `FREQ=WEEKLY;INTERVAL=2;BYDAY=${byDay}`;
      break;
    case 'monthly':
      baseRule = `FREQ=MONTHLY;BYMONTHDAY=${startAt.getDate()}`;
      break;
    case 'yearly':
      baseRule = 'FREQ=YEARLY';
      break;
    default:
      return null;
  }

  if (!baseRule || !recurrenceEndDate) return baseRule;

  const untilLocal = new Date(recurrenceEndDate);
  untilLocal.setHours(
    startAt.getHours(),
    startAt.getMinutes(),
    startAt.getSeconds(),
    0
  );
  return `${baseRule};UNTIL=${toIcalUtcTimestamp(untilLocal)}`;
}

export function previewExpenseOccurrences(
  rrule: string | null,
  startAt: Date,
  limit = 5
): string[] {
  if (!rrule) return [];
  try {
    const parsed = RRule.fromString(rrule);
    const withDtStart = new RRule({ ...parsed.origOptions, dtstart: startAt });
    return withDtStart.all((_, i) => i < limit).map((d) =>
      d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    );
  } catch {
    return [];
  }
}

export function formatRecurrenceLabel(preset: ExpenseRecurrencePreset): string {
  return (
    EXPENSE_RECURRENCE_OPTIONS.find((o) => o.value === preset)?.label ??
    'Does not repeat'
  );
}
